/**
 * resolveDisplayedStep
 *
 * Shared resolver used by every work-order render surface (WorkOrderSheet,
 * JobDetailsPopCard, TodayMobile, DispatchJobCard). Combines the immutable
 * generated baseline (`task.servicePlanStep`) with an optional visit-only
 * override (`task.servicePlanStepOverride`).
 *
 * MVP override fields:
 *   • items                — replaces baseline items when the key is present.
 *   • visitInstructions    — office-authored addendum ONLY. Never mutates
 *                            baseline.techNotes / products / areas.
 *
 * Presence rules (both fields are independent — an override may set one,
 * the other, or both):
 *   • `items` present (even []) → override.items replaces baseline.items.
 *   • `visitInstructions` present (even "") → visit-specific instructions
 *     section renders in the UI. Baseline.techNotes is untouched.
 *   • Empty values are INTENTIONAL — they do not fall back.
 *
 * Undo (DELETE /api/tasks/{id}/item-override) removes the entire override
 * object; the baseline-only work order returns.
 */
export function resolveDisplayedStep(task) {
  const baseline = task?.servicePlanStep || null;
  const override = task?.servicePlanStepOverride || null;

  const hasItemsOverride =
    !!override && typeof override === 'object' && Array.isArray(override.items);
  const hasVisitInstructionsOverride =
    !!override && typeof override === 'object' && typeof override.visitInstructions === 'string';
  const hasOverride = hasItemsOverride || hasVisitInstructionsOverride;

  const displayed = {
    label:                baseline?.label || '',
    products:             Array.isArray(baseline?.products) ? baseline.products : [],
    areas:                Array.isArray(baseline?.areas)    ? baseline.areas    : [],
    techNotes:            baseline?.techNotes   || '',
    officeNotes:          baseline?.officeNotes || '',
    estimatedDurationMin: baseline?.estimatedDurationMin ?? null,
    items:                hasItemsOverride
                            ? override.items
                            : (Array.isArray(baseline?.items) ? baseline.items : []),
    // Visit-specific instructions — override-only concept, no baseline
    // equivalent. Rendered as a separately-labeled section by consumers.
    visitInstructions:    hasVisitInstructionsOverride ? override.visitInstructions : null,
  };

  const overriddenFields = [];
  if (hasItemsOverride) overriddenFields.push('items');
  if (hasVisitInstructionsOverride) overriddenFields.push('visitInstructions');

  return {
    displayed,
    baseline,
    override,
    hasOverride,
    overriddenFields,
  };
}
